import type { V3 } from "backoffice-api"
import { TEXT_FALLBACK } from "ui-tools"
import { exists } from "utils"
import {
  isExactlyEqual,
  isPartiallyEqual,
  type Locale,
  parseLocale,
} from "./Locale"

export type TranslatedTexts =
  | V3["getProduct"]["data"]["title"]
  | null
  | undefined

type PickTranslatedTextProps = {
  texts: TranslatedTexts
  locale: string
  defaultLocale: string | undefined
  browserLocales: readonly string[]
  fallback?: string
}

export const pickTranslatedText = ({
  defaultLocale,
  texts,
  locale,
  browserLocales,
  fallback = TEXT_FALLBACK,
}: PickTranslatedTextProps) => {
  const textEntries = Object.entries(texts ?? {})
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(([key, text]) => [parseLocale(key), text] as const)

  // The reader's own language wins, then the company default, and only then the
  // browser — a browser guess must never override a language someone picked.
  // Each source is tried region-exact first and then on the language code alone,
  // so an `nb-NO` reader still gets text stored under `nb`.
  const wantedLocales = [locale, defaultLocale, ...browserLocales]
    .filter(exists)
    .map(parseLocale)

  for (const wantedLocale of wantedLocales) {
    const text = findText(textEntries, wantedLocale)
    if (text !== undefined) return text
  }

  return fallback
}

const findText = (
  textEntries: readonly (readonly [Locale, string])[],
  wantedLocale: Locale
) => {
  const exactMatch = textEntries.find(([textLocale]) =>
    isExactlyEqual(textLocale, wantedLocale)
  )
  if (exactMatch) return exactMatch[1]

  const partialMatch = textEntries.find(([textLocale]) =>
    isPartiallyEqual(textLocale, wantedLocale)
  )
  return partialMatch?.[1]
}
